'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState, useTransition } from 'react';
import { VariantProps } from 'tailwind-variants';

import { Article, UiArticleTileBase } from '@/fo/ui/article/tile/base';
import { UiKitPaginationBase } from '@/fo/ui/kit/pagination/base';
import { UiModHeadFixed } from '@/fo/ui/mod/head/fixed';

import {
  getPromotions,
  GetPromotionsProps,
  GetPromotionsResponse
} from '../api/getPromotions';
import { getPromotionsCount } from '../api/getPromotionsCount';
import { BrandFilter } from '../components/BrandFilter/BrandFilter';
import { TypeFilter } from '../components/TypeFilter/TypeFilter';
import { FILTERS_SLUG, PROMOTION_TYPES_VALUES } from '../constants';
import { FilterOption, SelectedFilters } from '../types';
import { prepareFilters } from '../util/prepareFilters';

import { styles } from './FeatureActionsPageList.styles';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FeatureActionsPageListProps {
  children?: React.ReactNode;
  color?: Variants['color'];
  onClick?: NativeElement['onClick'];
  data: GetPromotionsResponse & {
    promotions: Article[];
  };
}

let allPromotions: Article[] = [];

export function FeatureActionsPageList({
  data: initialData,
  ...props
}: FeatureActionsPageListProps) {
  const slots = styles();
  const t = useTranslations();
  const [isOpenBrandFilter, setIsOpenBrandFilter] = useState(false);
  const [isLoading, startTransition] = useTransition();
  const [promotions, setPromotions] = useState<Article[]>(
    initialData?.promotions || []
  );
  const [pagination, setPagination] = useState<{
    count: number;
    page: number;
  }>({
    count: initialData?.page_info?.total_pages || 0,
    page: initialData?.page_info?.current_page || 1
  });
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    [FILTERS_SLUG.TYPE]: PROMOTION_TYPES_VALUES.ALL,
    [FILTERS_SLUG.BRAND_NAME]: []
  });
  const selectedFiltersRef = useRef<SelectedFilters>(selectedFilters);

  const [promotionsCount, setPromotionsCount] = useState<number>(
    initialData?.total_count || 0
  );

  const onOpenChange = (open: boolean) => {
    if (open) {
      setIsOpenBrandFilter(true);
      return;
    }
    setSelectedFilters(selectedFiltersRef.current);
  };

  const fetchPromotions = async ({
    filters,
    currentPage
  }: {
    filters?: SelectedFilters;
    currentPage?: number;
  }) => {
    if (isLoading) return;
    startTransition(async () => {
      const response = await getPromotions({
        filter: prepareFilters(filters || selectedFilters),
        currentPage: currentPage || pagination.page
      });
      setPromotions(response.promotions);
      setPromotionsCount(response.total_count);
      setPagination({
        count: response.page_info?.total_pages || 0,
        page: response.page_info?.current_page || 1
      });
      if (isOpenBrandFilter) {
        setIsOpenBrandFilter(false);
      }
      selectedFiltersRef.current = filters || selectedFilters;
    });
  };

  if (!allPromotions.length) {
    allPromotions = promotions;
  }

  const handleTypeFilterChange = (option: FilterOption | null) => {
    if (!option) return;
    const newFilters = {
      ...selectedFilters,
      [option.slug]: option.value
    };

    setSelectedFilters(newFilters);
    fetchPromotions({
      filters: newFilters,
      currentPage: 1
    });
  };

  const handleBrandFilterChange = (
    slug: string,
    value: string,
    checked: boolean
  ) => {
    const newFilters = toggleBrandFilter(selectedFilters, slug, value, checked);
    setSelectedFilters(newFilters);
    fetchPromotions({
      filters: newFilters,
      currentPage: 1
    });
  };

  const resetBrandFilter = () => {
    const newFilters = { ...selectedFilters, [FILTERS_SLUG.BRAND_NAME]: [] };
    setSelectedFilters(newFilters);
    fetchPromotions({
      filters: newFilters,
      currentPage: 1
    });
  };

  const handlePaginationChange = (newPagination: {
    count: number;
    page: number;
  }) => {
    setPagination((prev) => ({
      ...prev,
      ...newPagination
    }));
    fetchPromotions({
      filters: selectedFilters,
      currentPage: newPagination.page
    });
  };

  const onLoadMore = async () => {
    if (isLoading) return;
    const nextPage = (pagination.page || 1) + 1;
    if (nextPage > pagination.count) return;
    const response = await getPromotions({
      filter: prepareFilters(selectedFilters),
      currentPage: nextPage
    });
    setPromotions((prev) => [...prev, ...response.promotions]);
    setPromotionsCount(response.total_count);
    setPagination({
      count: response.page_info?.total_pages || 0,
      page: response.page_info?.current_page || 1
    });
    if (isOpenBrandFilter) {
      setIsOpenBrandFilter(false);
    }
    selectedFiltersRef.current = selectedFilters;
  };

  const fetchPromotionsCount = async (props: GetPromotionsProps) => {
    const count = await getPromotionsCount(props);
    setPromotionsCount(count);
  };

  const onToggleChange = (slug: string, value: string, checked: boolean) => {
    const newFilters = toggleBrandFilter(selectedFilters, slug, value, checked);

    setSelectedFilters(newFilters);
    fetchPromotionsCount({
      filter: prepareFilters(newFilters)
    });
  };

  function toggleBrandFilter(
    filters: SelectedFilters,
    slug: string,
    value: string,
    checked: boolean
  ) {
    const updated = { ...filters };
    if (checked) {
      updated[slug] = [...(updated[slug] || []), value];
    } else {
      updated[slug] = (updated[slug] as string[]).filter((v) => v !== value);
      if (updated[slug].length === 0) updated[slug] = [];
    }
    return updated;
  }

  const groupPromotionsByType = (type: string) => {
    if (type === PROMOTION_TYPES_VALUES.ALL) {
      return allPromotions.length;
    }

    return allPromotions.filter((item) =>
      item.label?.find((label) => label.id === type)
    ).length;
  };

  return (
    <div
      className={slots.base()}
      {...props}
    >
      <UiModHeadFixed
        order={1}
        className={slots.headFixed()}
      >
        <div className={slots.filters()}>
          <TypeFilter
            selectedFilters={selectedFilters}
            handleTypeChange={handleTypeFilterChange}
            groupPromotionsByType={groupPromotionsByType}
          />
          <BrandFilter
            selectedFilters={selectedFilters}
            onChange={handleBrandFilterChange}
            onToggleChange={onToggleChange}
            onReset={resetBrandFilter}
            onShowPromotions={() =>
              fetchPromotions({
                currentPage: 1
              })
            }
            promotionsCount={promotionsCount}
            onOpenChange={onOpenChange}
            isOpenModal={isOpenBrandFilter}
            isLoading={isLoading}
          />
        </div>
      </UiModHeadFixed>
      <div className={slots.container()}>
        {pagination.count > 0 ? (
          <div className={slots.actionsList()}>
            {promotions.map((promotion) => (
              <UiArticleTileBase
                key={promotion.id}
                article={promotion}
              />
            ))}
          </div>
        ) : (
          <div className={slots.notFound()}>{t('productCatalog.notFound')}</div>
        )}
        <div className={slots.pagination()}>
          {pagination?.count > 1 && (
            <UiKitPaginationBase
              count={pagination.count}
              page={pagination.page}
              onChange={(page) =>
                handlePaginationChange({ count: pagination.count, page })
              }
              disabledNavigation
              onLoadMore={onLoadMore}
            />
          )}
        </div>
      </div>
    </div>
  );
}
